import { AddressService } from '@/common/services/address/address.service';
import { UrlService } from '@/common/services/url/url.service';
import { ValidationService } from '@/common/services/validation/validation.service';
import { isValid } from '@/common/utils/validation/common.utils';
import {
    emissionDateValidator,
    expirationDateValidator,
} from '@/common/utils/validation/datesValidators.utils';
import {
    CountriesResponse,
    Country,
} from '@/core/domain/address/models/countries-response.model';
import { StatesResponse } from '@/core/domain/address/models/states-response.model';
import { GetCountriesUseCase } from '@/core/domain/address/usecases/getCountries.usecase';
import { GetStatesUseCase } from '@/core/domain/address/usecases/getSates.usecase';
import { Documents } from '@/core/domain/official-documents/models/official-documents.model';
import { StoreDocumentsDataUseCase } from '@/core/domain/official-documents/usecases/storeDocuments.usecase';
import { selectPrecheckin } from '@/core/reservation/store/precheckin.selector';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import _ from 'lodash';

@Component({
    selector: 'fe-official-documents',
    templateUrl: './official-documents.component.html',
    styleUrls: ['./official-documents.component.scss'],
})
export class OfficialDocumentsComponent implements OnInit {
    @Input() submitForm = false;

    officialDocumentsForm!: FormGroup;
    additionalDriverForm!: FormGroup;

    isCheckboxChecked = false;
    showCheckboxError = false;
    lastName = '';
    reservationKey = '';
    isPassportSelected = false;
    isPassportSelected2 = false;

    additionalDriver = false;

    public returnDate: string | null = null;

    public today = new Date().toISOString();

    countries: any;

    private countryCode = '';
    private countryName = '';
    private stateName = '';

    private countryCode2 = '';
    private countryName2 = '';
    private stateName2 = '';

    public loadingCountries = false;
    public loadingStates = false;
    public loadingStates2 = false;

    public statesForOfficialDocuments: any[] = [];
    public statesForAdditionalDriver: any[] = [];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private readonly getCountriesUseCase: GetCountriesUseCase,
        private readonly getStatesUseCase: GetStatesUseCase,
        public addressService: AddressService,
        @Inject('INPUT_DATEPICKER') public inputDatePickerFormat: string,
        private storeDocumentsDataUseCase: StoreDocumentsDataUseCase,
        private url: UrlService,
        private readonly validation: ValidationService,
        private store: Store,
    ) {}

    ngOnInit(): void {
        this.checkingAdditionalDriver();

        this.officialDocumentsForm = this.fb.group({
            documentId: ['INE', [Validators.required]],
            idNumber: [''],
            idExpirationDate: [
                '',
                [
                    Validators.required,
                    expirationDateValidator(() => this.returnDate),
                ],
            ],
            driverNumber: ['', [Validators.required]],
            driverCountry: ['', [Validators.required]],
            driverEmissionState: ['', [Validators.required]],
            driverExpirationDate: [
                '',
                [
                    Validators.required,
                    expirationDateValidator(() => this.returnDate),
                ],
            ],
            driverEmissionDate: [
                '',
                [Validators.required, emissionDateValidator()],
            ],
        });

        this.updateIdNumberValidators(
            this.officialDocumentsForm.get('documentId')?.value,
            'officialDocumentsForm',
        );

        this.officialDocumentsForm
            .get('documentId')
            ?.valueChanges.subscribe((value) => {
                this.isPassportSelected = value === 'PASSPORT';
                this.updateIdNumberValidators(value, 'officialDocumentsForm');
            });

        if (this.additionalDriver) {
            this.additionalDriverForm = this.fb.group({
                documentId2: ['INE', [Validators.required]],
                idNumber2: [''],
                idExpirationDate2: [
                    '',
                    [
                        Validators.required,
                        expirationDateValidator(() => this.returnDate),
                    ],
                ],
                driverNumber2: ['', [Validators.required]],
                driverCountry2: ['', [Validators.required]],
                driverEmissionState2: ['', [Validators.required]],
                driverExpirationDate2: [
                    '',
                    [
                        Validators.required,
                        expirationDateValidator(() => this.returnDate),
                    ],
                ],
                driverEmissionDate2: [
                    '',
                    [Validators.required, emissionDateValidator()],
                ],
            });

            this.updateIdNumberValidators(
                this.additionalDriverForm.get('documentId2')?.value,
                'additionalDriverForm',
            );

            this.additionalDriverForm
                .get('documentId2')
                ?.valueChanges.subscribe((value) => {
                    this.isPassportSelected2 = value === 'PASSPORT';
                    this.updateIdNumberValidators(
                        value,
                        'additionalDriverForm',
                    );
                });
        }

        this.initCountries();
    }

    private checkingAdditionalDriver(): void {
        this.store.select(selectPrecheckin).subscribe((response) => {
            this.additionalDriver = this.areFieldsNotNull([
                response.email2,
                response.name2,
                response.phone2,
                response.lastName2,
            ]);
            this.returnDate = response.returnDate ?? null;
        });
    }

    private areFieldsNotNull(fields: unknown[]): boolean {
        return fields.every((field) => field !== null && field !== undefined);
    }

    updateIdNumberValidators(documentId: string, formType: string): void {
        let idNumberControl;

        if (formType === 'officialDocumentsForm') {
            idNumberControl = this.officialDocumentsForm.get('idNumber');
        } else if (formType === 'additionalDriverForm') {
            idNumberControl = this.additionalDriverForm.get('idNumber2');
        }

        if (documentId === 'INE') {
            idNumberControl?.setValidators([this.validation.ineValidator()]);
        } else if (documentId === 'PASSPORT') {
            idNumberControl?.setValidators([
                this.validation.passportValidator(),
            ]);
        }

        idNumberControl?.updateValueAndValidity();
    }

    private initCountries(): void {
        this.loadingCountries = true;
        this.getCountriesUseCase
            .execute()
            .subscribe((response: CountriesResponse) => {
                this.countries = response.data.countries;
                this.loadingCountries = false;
            });
    }

    public onChangeCountry(): void {
        const countryId = _.first(
            this.officialDocumentsForm.get('driverCountry')?.value,
        ) as number;

        this.statesForOfficialDocuments = [];
        this.officialDocumentsForm.get('driverEmissionState')?.setValue(null);

        if (!isValid(countryId)) {
            return;
        }

        const selectedCountry = this.countries.find(
            (country: Country) => country.id === countryId,
        );

        if (selectedCountry) {
            this.countryCode = selectedCountry.code;
            this.countryName = selectedCountry.name;
        }

        this.getStatesForOfficialDocuments(countryId);
    }

    private getStatesForOfficialDocuments(countryId: number) {
        this.loadingStates = true;
        this.getStatesUseCase
            .execute(countryId)
            .subscribe((response: StatesResponse) => {
                this.statesForOfficialDocuments = response.data?.states || [];
                this.loadingStates = false;
            });
    }

    public onChangeCountry2(): void {
        const countryId2 = _.first(
            this.additionalDriverForm.get('driverCountry2')?.value,
        ) as number;

        this.statesForAdditionalDriver = [];
        this.additionalDriverForm.get('driverEmissionState2')?.setValue(null);

        if (!isValid(countryId2)) {
            return;
        }

        const selectedCountry2 = this.countries.find(
            (country: Country) => country.id === countryId2,
        );

        if (selectedCountry2) {
            this.countryCode2 = selectedCountry2.code;
            this.countryName2 = selectedCountry2.name;
        }

        this.getStatesForAdditionalDriver(countryId2);
    }

    private getStatesForAdditionalDriver(countryId: number) {
        this.loadingStates2 = true;
        this.getStatesUseCase
            .execute(countryId)
            .subscribe((response: StatesResponse) => {
                this.statesForAdditionalDriver = response.data?.states || [];
                this.loadingStates2 = false;
            });
    }

    onCheckboxChange(): void {
        if (this.isCheckboxChecked) {
            this.showCheckboxError = false;
        }
    }

    private parseDateString(dateString: string): string {
        const [day, month, year] = dateString.split('/');
        const date = new Date(`${year}-${month}-${day}`);
        const formattedDate = date.toISOString().split('.')[0];
        return formattedDate;
    }

    private mapFormToDocuments(): Documents {
        const formValuesRenterDocuments = this.officialDocumentsForm.value;

        const driverState = this.statesForOfficialDocuments.find(
            (state) =>
                state.id === formValuesRenterDocuments.driverEmissionState[0],
        );

        const driverLicenseStateName = driverState
            ? driverState.name
            : formValuesRenterDocuments.driverEmissionState;

        const driverLicenseStateCode = driverState ? driverState.code : '';

        const idExpirationDateISO = this.parseDateString(
            formValuesRenterDocuments.idExpirationDate,
        );
        const driverExpirationDateISO = this.parseDateString(
            formValuesRenterDocuments.driverExpirationDate,
        );
        const driverEmissionDateISO = this.parseDateString(
            formValuesRenterDocuments.driverEmissionDate,
        );

        const documents: Documents = {
            renterDocuments: {
                ID: {
                    type: formValuesRenterDocuments.documentId,
                    number: formValuesRenterDocuments.idNumber,
                    expirationDate: idExpirationDateISO,
                },
                driverLicense: {
                    number: formValuesRenterDocuments.driverNumber,
                    countryCode: this.countryCode,
                    countryName: this.countryName,
                    state: driverLicenseStateName,
                    stateCode: driverLicenseStateCode,
                    expirationDate: driverExpirationDateISO,
                    issueDate: driverEmissionDateISO,
                },
            },
        };

        if (this.additionalDriver) {
            const formValuesAdditionalPilot = this.additionalDriverForm.value;

            const additionalPilotState = this.statesForAdditionalDriver.find(
                (state) =>
                    state.id ===
                    formValuesAdditionalPilot.driverEmissionState2[0],
            );

            const additionalPilotStateName = additionalPilotState
                ? additionalPilotState.name
                : formValuesAdditionalPilot.driverEmissionState2;

            const additionalPilotStateCode = additionalPilotState
                ? additionalPilotState.code
                : '';

            const idExpirationDateISO2 = this.parseDateString(
                formValuesAdditionalPilot.idExpirationDate2,
            );
            const driverExpirationDateISO2 = this.parseDateString(
                formValuesAdditionalPilot.driverExpirationDate2,
            );
            const driverEmissionDateISO2 = this.parseDateString(
                formValuesAdditionalPilot.driverEmissionDate2,
            );

            documents.additionalPilot = {
                ID: {
                    type: formValuesAdditionalPilot.documentId2,
                    number: formValuesAdditionalPilot.idNumber2,
                    expirationDate: idExpirationDateISO2,
                },
                driverLicense: {
                    number: formValuesAdditionalPilot.driverNumber2,
                    countryCode: this.countryCode2,
                    countryName: this.countryName2,
                    state: additionalPilotStateName,
                    stateCode: additionalPilotStateCode,
                    expirationDate: driverExpirationDateISO2,
                    issueDate: driverEmissionDateISO2,
                },
            };
        }

        return documents;
    }

    onSubmit(): void {
        const officialFormValid = this.officialDocumentsForm.valid;
        const additionalFormValid =
            !this.additionalDriver || this.additionalDriverForm.valid;

        if (
            officialFormValid &&
            additionalFormValid &&
            this.isCheckboxChecked
        ) {
            this.showCheckboxError = false;
            const documents = this.mapFormToDocuments();
            const reservationCode =
                this.route.snapshot.paramMap.get('resevationCode');

            this.storeDocumentsDataUseCase
                .execute(documents)
                .subscribe((result) => {
                    if (result === true && reservationCode) {
                        this.router.navigate([
                            this.url.format(
                                'pre-check-in/reservations/' +
                                    reservationCode +
                                    '/billing_data',
                            ),
                        ]);
                    }
                });
        } else {
            if (this.additionalDriver) {
                this.additionalDriverForm.markAllAsTouched();
            }

            this.officialDocumentsForm.markAllAsTouched();
            this.showCheckboxError = !this.isCheckboxChecked;
        }
    }
}
